import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { MonitorSmartphone, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCarWash } from '@/hooks/use-carwash'
import { useInstallPrompt } from '@/hooks/use-install-prompt'
import { buttonClasses } from '@/components/kit/button'
import { NAV_ITEMS } from '@/components/app/nav-items'
import { useNavCounts } from '@/components/app/use-nav-counts'
import { BusinessLogo } from '@/components/app/business-logo'
import { InstallSheet } from '@/components/app/install-sheet'
import { LiveDate, LiveTime } from '@/components/app/live-clock'

export const Sidebar: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const { state } = useCarWash()
    const counts = useNavCounts()
    const { state: installState } = useInstallPrompt()
    const [installOpen, setInstallOpen] = useState(false)

    const businessName = state?.settings.name ?? 'Washline'

    return (
        <aside className='fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-sidebar-border bg-sidebar lg:flex print:hidden'>
            <div className='flex h-16 items-center gap-3 border-b border-sidebar-border px-5'>
                <BusinessLogo logo={state?.settings.logo} name={businessName} className='size-9' />

                <div className='min-w-0'>
                    <p className='truncate font-heading text-[15px] leading-tight font-bold text-foreground'>
                        {businessName}
                    </p>

                    <p className='text-xs font-medium text-muted-foreground'>
                        Washline operations
                    </p>
                </div>
            </div>

            <div className='p-4'>
                <Link href='/vehicles/new' className={buttonClasses({ size: 'lg', className: 'w-full shadow-primary' })}>
                    <Plus aria-hidden='true' />
                    New vehicle
                </Link>
            </div>

            <nav aria-label='Main' className='flex-1 px-3'>
                <ul className='space-y-1'>
                    {NAV_ITEMS.map(item => {
                        const active = item.isActive(pathname)
                        const badge = counts[item.href]
                        const Icon = item.icon

                        return (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={cn(
                                        'flex h-11 items-center gap-3 rounded-lg px-3 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                                        active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-slate-600 hover:bg-slate-100 hover:text-foreground'
                                    )}
                                >
                                    <Icon className='size-5' aria-hidden='true' />

                                    <span className='flex-1'>
                                        {item.label}
                                    </span>

                                    {badge && badge.count > 0 && (
                                        <span className='rounded-full bg-orange-100 px-2 py-0.5 text-xs font-bold text-orange-800 tabular-nums'>
                                            {badge.count}
                                            <span className='sr-only'>{` ${badge.label}`}</span>
                                        </span>
                                    )}
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </nav>

            <div className='space-y-3 border-t border-sidebar-border p-4'>
                <div className='rounded-xl bg-surface px-3.5 py-3'>
                    <LiveTime className='block font-heading text-2xl font-bold text-foreground tabular-nums' />
                    <LiveDate long className='block text-xs font-medium text-muted-foreground' />
                </div>

                {installState !== 'installed' && (
                    <button
                        type='button'
                        onClick={() => setInstallOpen(true)}
                        className='flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                    >
                        <MonitorSmartphone className='size-[18px]' aria-hidden='true' />
                        Install app
                    </button>
                )}
            </div>

            <InstallSheet open={installOpen} onClose={() => setInstallOpen(false)} />
        </aside>
    )
}
